import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  Paper,
  BottomDrawer,
  ConfirmDialog,
  DetailField,
  EntityDetailShell,
  formatAddress,
} from '@vipso/shared';
import {
  useGetVendorQuery,
  useAddUpdateVendorMutation,
  useDeleteVendorMutation,
} from '../api/tenantApi';
import {
  VendorForm,
  createEmptyVendorForm,
  isVendorPhoneValid,
  vendorFormValues,
  type VendorFormValues,
} from '../features/vendors/VendorForm';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';
import { EntityApprovalPanel } from '../components/EntityApprovalPanel';
import { useModuleTerminology } from '../hooks/useModuleLabel';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useApprovalModuleAccess } from '../hooks/useApprovalModuleAccess';

export function VendorDetailPage() {
  const access = useModuleAccess('vendor');
  const approvalAccess = useApprovalModuleAccess();
  const { t } = useTranslation();
  const terms = useModuleTerminology('vendor');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetVendorQuery(id!, { skip: !id });
  const [save, { isLoading: saving }] = useAddUpdateVendorMutation();
  const [remove, { isLoading: deleting }] = useDeleteVendorMutation();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] = useState<VendorFormValues>(createEmptyVendorForm);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const vendor = data?.getVendor;

  function openEdit() {
    if (!vendor) return;
    setPhoneError(null);
    setForm(vendorFormValues(vendor));
    setEditOpen(true);
  }

  async function handleSave() {
    if (!vendor) return;
    if (!isVendorPhoneValid(form)) {
      setPhoneError(t('phone.invalid'));
      return;
    }
    setPhoneError(null);
    await save({
      id: vendor.id,
      name: form.name,
      phone: form.phone || null,
      email: form.email || null,
    }).unwrap();
    setEditOpen(false);
  }

  async function handleDelete() {
    if (!vendor) return;
    await remove(vendor.id).unwrap();
    setConfirmOpen(false);
    navigate('/vendors');
  }

  return (
    <EntityDetailShell
      isLoading={isLoading && !data?.getVendor}
      notFound={isError || !vendor}
      notFoundTitle={t('terminology.notFound', { entity: terms.singular })}
      backLabel={t('terminology.backToList', { entity: terms.plural })}
      onBack={() => navigate('/vendors')}
      title={vendor?.name ?? ''}
      description={t('terminology.details', { entity: terms.singular })}
      actions={
        access.canUpdate || access.canDelete ? (
          <Group gap="sm">
            {access.canUpdate ? (
              <Button variant="light" onClick={openEdit}>
                {t('common.edit')}
              </Button>
            ) : null}
            {access.canDelete ? (
              <Button color="red" variant="light" onClick={() => setConfirmOpen(true)}>
                {t('common.delete')}
              </Button>
            ) : null}
          </Group>
        ) : undefined
      }
    >
      <Paper p="lg" withBorder radius="md">
        <Stack gap="md">
          <DetailField label={t('common.phone')} value={vendor?.phone} />
          <DetailField label={t('common.email')} value={vendor?.email} />
          {approvalAccess.vendor ? (
            <DetailField
              label={t('approval.status')}
              value={<ApprovalStatusBadge status={vendor?.approvalStatus} />}
            />
          ) : null}
          <DetailField label={t('vendors.openingBalance')} value={vendor?.openingBalance ?? 0} />
          <DetailField label={t('common.address')} value={formatAddress(vendor?.address)} />
        </Stack>
      </Paper>

      <EntityApprovalPanel
        subjectType="VENDOR"
        subjectId={vendor?.id}
        entityApprovalStatus={vendor?.approvalStatus}
        enabled={approvalAccess.vendor}
      />

      <Button
        onClick={() => navigate(`/orders?vendorIds=${vendor?.id}`)}
        style={{ alignSelf: 'flex-start' }}
      >
        {t('common.transactions')}
      </Button>

      <BottomDrawer opened={editOpen} onClose={() => setEditOpen(false)} title={t('terminology.edit', { entity: terms.singular })}>
        <Stack gap="md">
          <VendorForm
            value={form}
            phoneError={phoneError}
            onChange={(next) => {
              setPhoneError(null);
              setForm(next);
            }}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={saving}
              onClick={() => void handleSave()}
              disabled={!form.name.trim() || !isVendorPhoneValid(form)}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>

      <ConfirmDialog
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void handleDelete()}
        title={t('terminology.deleteConfirm', { entity: terms.singular })}
        message={t('common.deleteMessage', { name: vendor?.name ?? '' })}
        confirmLabel={t('common.delete')}
        loading={deleting}
        danger
      />
    </EntityDetailShell>
  );
}
